import { MutableRefObject, useEffect, useRef, useState } from "react";

export const RANDOM_BACKGROUND_COLOR = ["#744CF6", "#D74CF6", "#4F72FF", "#FFA44F"] as const;

export type AvatarBackgroundColor = (typeof RANDOM_BACKGROUND_COLOR)[number];

interface UseContainerResizeDoneOptions {
    delay?: number;
    onReady?: () => void;
}

export const useContainerResizeDone = (
    containerRef: React.RefObject<HTMLElement | null>,
    options: UseContainerResizeDoneOptions,
): [boolean, (ready: boolean) => void] => {
    const { delay = 500, onReady } = options;
    const [isReady, setIsReady] = useState(false);
    const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (!containerRef.current) {
            return;
        }
        const resizeObserver = new ResizeObserver(_entries => {
            // 清除之前的定时器
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current);
            }

            // 等待尺寸变化结束
            resizeTimeoutRef.current = setTimeout(() => {
                setIsReady(true);
                onReady?.();
            }, delay);
        });

        resizeObserver.observe(containerRef.current);

        return () => {
            resizeObserver.disconnect();
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current);
            }
        };
    }, [delay, onReady]);

    return [isReady, setIsReady];
};

interface UseInViewOptions {
    root?: Element | null;
    rootMargin?: string;
    threshold?: number | number[];
}

export const useIsInView = (
    options: UseInViewOptions = {},
): [MutableRefObject<Element | null>, boolean] => {
    const elementRef = useRef<Element | null>(null);
    const [isInView, setIsInView] = useState(false);

    useEffect(() => {
        const element = elementRef.current;
        if (!element) {
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsInView(entry.isIntersecting);
            },
            {
                root: options.root ?? null,
                rootMargin: options.rootMargin ?? "0px",
                threshold: options.threshold ?? 0,
            },
        );

        observer.observe(element);

        return () => {
            observer.disconnect();
        };
    }, [options.root, options.rootMargin, options.threshold]);

    return [elementRef, isInView];
};

export const useGenerateAvatar = (
    name: string,
    color?: AvatarBackgroundColor,
    size = 382,
): HTMLCanvasElement => {
    const colorIndexRef = useRef<number>(
        Math.floor(Math.random() * RANDOM_BACKGROUND_COLOR.length),
    );

    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d")!;

    const bgColor = color ? color : RANDOM_BACKGROUND_COLOR[colorIndexRef.current];
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, size, size);

    if (name.length === 0) {
        return canvas;
    }

    ctx.fillStyle = "#fff";
    ctx.font = `${size * 0.3}px 思源黑体`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    let initials = "";
    let count = 0;
    // 中文汉字、中文符号、全角符号、CJK标点都算2个字符
    const cnChar = /[\u4e00-\u9fff\u3400-\u4dbf\u3000-\u303F\uff00-\uffef]/;
    // @ts-ignore
    for (const char of name.replace(/\s+/g, "")) {
        // 中文字符范围
        if (cnChar.test(char)) {
            if (count + 2 > 4) {
                break;
            }
            initials += char;
            count += 2;
        } else {
            if (count + 1 > 4) {
                break;
            }
            initials += char;
            count += 1;
        }
    }
    const fix = ctx.measureText(initials).actualBoundingBoxDescent / 2;
    ctx.fillText(initials, size / 2, size / 2 + fix / 2);
    return canvas;
};
